import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { CONSTANTS } from 'src/app/constants';
import { AnalyzersService } from 'src/app/providers/services/analyzers.service';
import { ContainersService } from 'src/app/providers/services/containers.service';
import { LoaderService } from 'src/app/providers/services/loader.service';
import { MissionsService } from 'src/app/providers/services/missions.service';
import { NavigationService } from 'src/app/providers/services/navigation.service';
import { SkidsService } from 'src/app/providers/services/skids.service';
import { TeamsService } from 'src/app/providers/services/teams.service';
import { Mission } from 'src/app/storage/models/mission.model';

const TEAM_ROLES = CONSTANTS.TEAM.ROLES;
const STATUS = CONSTANTS.MISSION.CONFIG_STATUS;

@Component({
    selector: 'app-mission-config',
    templateUrl: './mission-config.component.html',
    styleUrls: ['./mission-config.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MissionConfigComponent implements OnInit, OnDestroy {

  mission: Mission;
  private missionSubscription: Subscription;

  constructor(
    private missionsService: MissionsService,
    private containersService: ContainersService,
    private skidsService: SkidsService,
    private analyzersService: AnalyzersService,
    private teamsService: TeamsService,
    private navigationService: NavigationService,
    private loader: LoaderService
  ) { }

  async ngOnInit(): Promise<void> {
    this.missionSubscription = this.missionsService.selectedMission$.subscribe(async (mission: Mission) => {
      // Get selected mission or navigate back to mission selection
      (mission !== null) ?
        await this.init(mission) :
        await this.navigationService.navigateFromHome(CONSTANTS.ROUTES.MISSION_SELECTION);
    });
  }

  ngOnDestroy(): void {
    if (this.missionSubscription) { this.missionSubscription.unsubscribe(); }
  }

  async init(mission: Mission): Promise<void> {

    this.loader.show();

    // Set default support teams
    if (mission.supportstatus === STATUS.NOT_STARTED) {
      const defaultGroupAreSet = await this.teamsService.setDefaultSupportTeams(mission.missionId);
      if (defaultGroupAreSet) {
        const isSupportStatusUpdated = await this.missionsService.updateMissionSupportStatus(mission.missionId, STATUS.IN_PROGRESS);
        if (isSupportStatusUpdated) {
          mission.supportstatus = STATUS.IN_PROGRESS;
        } else {
          await this.navigationService.navigateFromHome(CONSTANTS.ROUTES.MISSION_SELECTION);
          return;
        }
      } else {
        await this.navigationService.navigateFromHome(CONSTANTS.ROUTES.MISSION_SELECTION);
        return;
      }
    }

    // Set mission teams, containers & skids, navigate back to mission selection if couldn't be fetched
    const missionTeams = await this.teamsService.getMissionTeams({ missionId: mission.missionId, rolename: TEAM_ROLES.TECHNICIAN });
    const missionContainers = await this.containersService.getMissionContainers(mission.missionId);
    const missionSkids = await this.skidsService.getMissionSkids(mission.missionId);
    const missionAnalyzers = await this.analyzersService.getMissionAnalyzers(mission.missionId);
    const missionSupportTeams = await this.teamsService.getMissionTeams({ missionId: mission.missionId, rolename: TEAM_ROLES.REMOTE_SUPERVISION });

    if (missionTeams == null || missionContainers == null || missionSkids == null || missionAnalyzers == null || missionSupportTeams == null) {
      await this.navigationService.navigateFromHome(CONSTANTS.ROUTES.MISSION_SELECTION);
      return;
    }
    
    this.teamsService.missionTeams$.next(missionTeams);
    this.containersService.missionContainers$.next(missionContainers);
    this.skidsService.missionSkids$.next(missionSkids);
    this.analyzersService.missionAnalyzers$.next(missionAnalyzers);
    this.teamsService.missionSupportTeams$.next(missionSupportTeams);

    this.loader.hide();

    this.mission = mission;
  }

  async navigateToMissionInfo(): Promise<void> {
    await this.navigationService.navigateFromMissionConfig(CONSTANTS.ROUTES.MISSION_INFO);
  }

  async navigateToTeam(): Promise<void> {
    await this.navigationService.navigateFromMissionConfig(CONSTANTS.ROUTES.TEAM_CONFIG);
  }

  async navigateToProduct(): Promise<void> {
    await this.navigationService.navigateFromMissionConfig(CONSTANTS.ROUTES.PRODUCT_CONFIG);
  }

  async navigateToMaterial(): Promise<void> {
    await this.navigationService.navigateFromMissionConfig(CONSTANTS.ROUTES.MATERIAL_CONFIG);
  }

  async navigateToSupport(): Promise<void> {
    await this.navigationService.navigateFromMissionConfig(CONSTANTS.ROUTES.SUPPORT_CONFIG);
  }

  getStatusColor(status: number, canBeBlocked: boolean): string {
    if (canBeBlocked && this.mission.checkingIsDone) { return "#e30040"; }
    const STATUS = CONSTANTS.MISSION.CONFIG_STATUS;
    switch (status) {
      case STATUS.IN_PROGRESS:
        return "#003478";
      case STATUS.COMPLETED:
        return "#1ce29b";
      case STATUS.BLOCKED:
        return "#e30040";
      case STATUS.NOT_STARTED:
      default:
        return "#8b9093";
    }
  }

}
